import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';
import * as Sentry from '@sentry/react';
import BEM from 'common/bem';
import type { ArchivingExportSummary } from 'js-sdk/src/apis/ArchivingAPI';

const classes = BEM.with('ExportHistory');

// Statuses that mean a job might still transition — while any row is in one of these, we poll for
// updates instead of requiring the user to manually refresh to see queued/processing become
// ready/failed.
const PENDING_STATUSES = new Set(['queued', 'processing']);
const POLL_INTERVAL_MS = 8000;
// After this many consecutive failed background polls, surface a notice — a single transient
// failure isn't worth alarming the user over, but a run of them means the row's real status is no
// longer being tracked and silently showing "Processing" forever would be misleading.
const POLL_FAILURE_NOTICE_THRESHOLD = 3;

type ExportHistoryClient = {
  archiving: {
    listExports: (
      organizationId: string
    ) => Promise<{ data: { exports: ArchivingExportSummary[] } }>;
    getExportDownloadUrl: (
      organizationId: string,
      jobId: string
    ) => Promise<{ data: { downloadUrl: string } }>;
  };
};

type ExportHistoryProps = {
  client: ExportHistoryClient | null;
  currentOrganizationId: string;
};

function statusLabel(status: string): string {
  switch (status) {
    case 'queued':
      return 'Queued';
    case 'processing':
      return 'Processing';
    case 'ready':
      return 'Ready';
    case 'failed':
      return 'Failed';
    default:
      return status;
  }
}

export default function ExportHistory({ client, currentOrganizationId }: ExportHistoryProps) {
  const [exports, setExports] = useState<ArchivingExportSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadingJobId, setDownloadingJobId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [pollFailureNotice, setPollFailureNotice] = useState(false);
  const hasPendingExports = useRef(false);
  const consecutivePollFailures = useRef(0);

  const loadExports = useCallback(
    async (isBackgroundRefresh = false) => {
      if (!client) return;

      if (!isBackgroundRefresh) {
        setIsLoading(true);
        setError(null);
      }
      try {
        const response = await client.archiving.listExports(currentOrganizationId);
        const results = response?.data?.exports || [];
        setExports(results);
        hasPendingExports.current = results.some((item) => PENDING_STATUSES.has(item.jobStatus));
        consecutivePollFailures.current = 0;
        setPollFailureNotice(false);
      } catch (err) {
        if (!isBackgroundRefresh) {
          setError('Something went wrong loading export history. Please try again.');
          return;
        }

        // A background poll fails silently to the user by design for the first couple of
        // attempts (a single blip shouldn't alarm anyone), but it must never fail silently to
        // telemetry — without this, a status stuck on "queued"/"processing" forever because
        // polling itself broke looks identical to a job that's genuinely still processing.
        consecutivePollFailures.current += 1;
        Sentry.logger.warn(
          Sentry.logger.fmt`[ExportHistory] Background export status poll failed`,
          { error: err, consecutiveFailures: consecutivePollFailures.current }
        );
        if (consecutivePollFailures.current >= POLL_FAILURE_NOTICE_THRESHOLD) {
          setPollFailureNotice(true);
        }
      } finally {
        if (!isBackgroundRefresh) {
          setIsLoading(false);
        }
      }
    },
    [client, currentOrganizationId]
  );

  useEffect(() => {
    loadExports();
  }, [loadExports]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (hasPendingExports.current) {
        loadExports(true);
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadExports]);

  async function onDownload(jobId: string) {
    if (!client) return;

    setDownloadingJobId(jobId);
    setDownloadError(null);
    try {
      const response = await client.archiving.getExportDownloadUrl(currentOrganizationId, jobId);
      const downloadUrl = response?.data?.downloadUrl;
      if (downloadUrl) {
        window.open(downloadUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      setDownloadError('Something went wrong generating the download link. Please try again.');
    } finally {
      setDownloadingJobId(null);
    }
  }

  return (
    <div className={classes()}>
      <div className={classes('header')}>
        <div className={classes('header-text')}>
          <h2 className={classes('title')}>Export History</h2>
          <p className={classes('subtitle')}>
            Track and download exports triggered for this organization
          </p>
        </div>
        <button
          className={classes('refresh-button')}
          data-test-id="export-history-refresh"
          disabled={isLoading}
          onClick={() => loadExports()}
          type="button"
        >
          Refresh
        </button>
      </div>

      {error && <div className={classes('error')}>{error}</div>}
      {downloadError && <div className={classes('error')}>{downloadError}</div>}
      {pollFailureNotice && (
        <div className={classes('poll-failure-notice')} data-test-id="export-history-poll-failure">
          Having trouble checking for status updates. Click Refresh to check manually.
        </div>
      )}

      {isLoading && (
        <div className={classes('loading')} data-test-id="export-history-loading">
          Loading export history…
        </div>
      )}

      {!isLoading && !error && exports.length === 0 && (
        <div className={classes('empty-state')} data-test-id="export-history-empty">
          No exports yet. Exports you generate from Export Builder will show up here.
        </div>
      )}

      {!isLoading && exports.length > 0 && (
        <ul className={classes('list')} data-test-id="export-history-list">
          {exports.map((item) => (
            <li className={classes('item')} key={item.exportJobId}>
              <div className={classes('item-main')}>
                <span className={classes('item-name')}>{item.exportName || item.exportJobId}</span>
                <span className={classes('item-meta')}>
                  Created {moment(item.createdAt).format('MMM D, YYYY h:mm A')} · Expires{' '}
                  {moment(item.expiresAt).format('MMM D, YYYY h:mm A')}
                </span>
              </div>
              <div className={classes('item-actions')}>
                <span
                  className={classes('status-badge', { [item.jobStatus]: true })}
                  data-test-id={`export-history-status-${item.exportJobId}`}
                >
                  {statusLabel(item.jobStatus)}
                </span>
                {item.jobStatus === 'ready' && (
                  <button
                    className={classes('download-button')}
                    data-test-id={`export-history-download-${item.exportJobId}`}
                    disabled={downloadingJobId === item.exportJobId}
                    onClick={() => onDownload(item.exportJobId)}
                    type="button"
                  >
                    {downloadingJobId === item.exportJobId ? 'Preparing…' : 'Download'}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
